import { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IoCartOutline, IoEyeOutline } from 'react-icons/io5';
import { notifications } from '@mantine/notifications';
import { CachedImage, Checkbox, DotMenu, DoubleCheckModal, type DotMenuItem } from '@shared/ui';
import { useSetProductFlag, useDeleteProduct, useRemoveFromCategory } from '../../hooks/useProducts';
import { ProductFlagType } from '../../types/product-flag-type.enum';
import AddToSetModal from '../AddToSetModal';
import AddToCategoryModal from '../AddToCategoryModal';
import type { ProductCardResponse } from '../../types';
import { productThumbSrc } from '../../utils';
import ProductPrice from './ProductPrice';
import ProductCategories from './ProductCategories';
import ProductBadges from './ProductBadges';
import styles from './ProductCard.module.css';

interface ProductCardProps {
  product: ProductCardResponse;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: (productId: number, selected: boolean) => void;
  hideMenu?: boolean;
  onEdit?: (productId: number) => void;
}

export default function ProductCard({
  product,
  isSelectionMode = false,
  isSelected = false,
  onSelect,
  hideMenu = false,
  onEdit: onEditProp,
}: ProductCardProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const isSellerAdmin = useMemo(() => {
    try {
      const raw = localStorage.getItem('luxus_admin');
      if (!raw) return false;
      const p = JSON.parse(raw) as { role?: string };
      return p.role === 'SELLER_ADMIN';
    } catch {
      return false;
    }
  }, [location.pathname]);
  const setProductOutOfStock = useSetProductFlag(ProductFlagType.OUT_OF_STOCK);
  const deleteProduct = useDeleteProduct();
  const removeFromCategory = useRemoveFromCategory();
  const [isAddToSetModalOpen, setIsAddToSetModalOpen] = useState(false);
  const [isAddToCategoryModalOpen, setIsAddToCategoryModalOpen] = useState(false);
  const [isDeleteCategoryModalOpen, setIsDeleteCategoryModalOpen] = useState(false);
  const [isDeleteProductModalOpen, setIsDeleteProductModalOpen] = useState(false);
  const [categoryToRemove, setCategoryToRemove] = useState<{ id: number; name: string } | null>(null);

  const firstImage = product.images && product.images.length > 0 ? product.images[0] : null;

  // Преобразуем значения в числа, если они пришли как строки
  const price = typeof product.price === 'string' ? parseFloat(product.price) : product.price;
  const hasDiscount = !!(product.discount && product.discountedPrice);

  // Определяем, откуда переходим, и передаем текущий путь в state
  const linkState = { from: location.pathname };

  const handleEdit = () => {
    if (onEditProp) {
      onEditProp(product.id);
    } else {
      navigate(`/products/${product.id}/edit`, { state: linkState });
    }
  };

  const handleSetOutOfStock = async () => {
    try {
      await setProductOutOfStock.mutateAsync({
        id: product.id,
        value: !product.isOutOfStock,
      });

      notifications.show({
        title: 'Успешно',
        message: product.isOutOfStock ? 'Товар помечен как в наличии' : 'Товар помечен как закончившийся',
        color: 'green',
      });
    } catch (error) {
      console.error('Ошибка при изменении статуса товара:', error);
      notifications.show({
        title: 'Ошибка',
        message: 'Ошибка при изменении статуса товара',
        color: 'red',
      });
    }
  };

  const handleAddToSet = () => {
    setIsAddToSetModalOpen(true);
  };

  const handleCloseAddToSetModal = () => {
    setIsAddToSetModalOpen(false);
  };

  const handleAddToCategory = () => {
    setIsAddToCategoryModalOpen(true);
  };

  const handleCloseAddToCategoryModal = () => {
    setIsAddToCategoryModalOpen(false);
  };

  const handleRemoveFromCategory = (categoryId: number, categoryName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setCategoryToRemove({ id: categoryId, name: categoryName });
    setIsDeleteCategoryModalOpen(true);
  };

  const confirmRemoveFromCategory = async () => {
    if (!categoryToRemove) return;

    try {
      await removeFromCategory.mutateAsync({
        productId: product.id,
        categoryId: categoryToRemove.id,
      });

      notifications.show({
        title: 'Успешно',
        message: 'Товар успешно удален из категории',
        color: 'green',
      });

      setIsDeleteCategoryModalOpen(false);
      setCategoryToRemove(null);
    } catch (error: any) {
      console.error('Ошибка при удалении товара из категории:', error);
      notifications.show({
        title: 'Ошибка',
        message: error?.response?.data?.message || 'Ошибка при удалении товара из категории',
        color: 'red',
      });
    }
  };

  const cancelRemoveFromCategory = () => {
    setIsDeleteCategoryModalOpen(false);
    setCategoryToRemove(null);
  };

  const confirmDelete = async () => {
    try {
      await deleteProduct.mutateAsync(product.id);
      notifications.show({
        title: 'Успешно',
        message: 'Товар успешно удален',
        color: 'green',
      });
      setIsDeleteProductModalOpen(false);
    } catch (error: unknown) {
      const responseData = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data
        : undefined;
      const errorMessage = responseData?.message ?? (error instanceof Error ? error.message : 'Неизвестная ошибка');
      notifications.show({
        title: 'Ошибка',
        message: errorMessage,
        color: 'red',
      });
      setIsDeleteProductModalOpen(false);
    }
  };

  const cancelDelete = () => {
    setIsDeleteProductModalOpen(false);
  };

  const menuItems: DotMenuItem[] = [
    ...(isSellerAdmin ? [{ label: 'Редактировать', onClick: handleEdit }] : []),
    // {
    //   label: product.isHidden ? 'Показать' : 'Скрыть',
    //   onClick: handleHide,
    // },
    ...(isSellerAdmin
      ? [
        {
          label: product.isOutOfStock ? 'В наличии' : 'Закончился',
          onClick: handleSetOutOfStock,
        },
      ]
      : []),
    {
      label: 'Добавить в подборку',
      onClick: handleAddToSet,
    },
    ...(isSellerAdmin
      ? [{ label: 'Добавить в категорию', onClick: handleAddToCategory }]
      : []),
    // {
    //   label: 'Удалить',
    //   onClick: handleDelete,
    //   isDanger: true,
    // },
  ];

  const cardContent = (
    <>
      {firstImage && (
        <div
          className={`${styles.productCardImage} ${product.isHidden || product.isOutOfStock ? styles.productCardImageHidden : ''}`}
        >
          <CachedImage
            src={productThumbSrc(firstImage) || firstImage.url}
            alt={product.name}
            loading="lazy"
            decoding="async"
          />
          <ProductBadges
            productId={product.id}
            hasDiscount={hasDiscount}
            isPopular={product.isPopular}
            isNew={product.isNew}
            isHidden={product.isHidden}
            isOutOfStock={product.isOutOfStock}
            customSets={product.customSets}
          />
        </div>
      )}

      <div className={styles.productCardContent}>
        <div className={styles.productCardIdWrapper}>
          <div className={styles.productCardIdWrapperInner}>
            <span className={styles.productCardId}>ID: {product.id}</span>
            <div className={styles.productCardStats}>
              {product.rating != null && (
                <span className={styles.productCardRating}>⭐ {product.rating}</span>
              )}
              <span className={styles.productCardStat} title="Куплено">
                <IoCartOutline /> {product.purchaseCount ?? 0}
              </span>
              <span className={styles.productCardStat} title="Просмотрено">
                <IoEyeOutline /> {product.viewedCount ?? 0}
              </span>
            </div>
          </div>
          <h3 className={styles.productCardTitle}> {product.name} </h3>
        </div>

        <ProductCategories
          categories={product.categories}
          onRemoveClick={isSellerAdmin ? handleRemoveFromCategory : undefined}
          isRemoving={removeFromCategory.isPending}
        />

        <div className={styles.productCardFooter}>
          <ProductPrice
            price={price}
            discount={product.discount}
            discountedPrice={product.discountedPrice}
          />

        </div>
      </div>
    </>
  );

  return (
    <div className={styles.productCardWrapper}>
      {isSelectionMode ? (
        <div
          className={styles.productCard}
          onClick={() => onSelect?.(product.id, !isSelected)}
          style={{ cursor: 'pointer' }}
        >
          {cardContent}
        </div>
      ) : (
        <Link to={`/products/${product.id}`} state={linkState} className={styles.productCard}>
          {cardContent}
        </Link>
      )}
      {(isSelectionMode || !hideMenu) && (
        <div className={styles.productCardMenu} onClick={(e) => e.stopPropagation()}>
          {isSelectionMode ? (
            <Checkbox
              checked={isSelected}
              onChange={(e) => onSelect?.(product.id, e.currentTarget.checked)}
            />
          ) : (
            <DotMenu items={menuItems} />
          )}
        </div>
      )}

      {isAddToSetModalOpen && <AddToSetModal
        isOpen={isAddToSetModalOpen}
        onClose={handleCloseAddToSetModal}
        productIds={[product.id]}
      />}

      {isSellerAdmin && isAddToCategoryModalOpen && (
        <AddToCategoryModal
          isOpen={isAddToCategoryModalOpen}
          onClose={handleCloseAddToCategoryModal}
          productId={product.id}
          currentCategoryIds={product.categories?.map(pc => pc.category.id) || []}
        />
      )}

      {isDeleteCategoryModalOpen && <DoubleCheckModal
        isOpen={isDeleteCategoryModalOpen}
        onClose={cancelRemoveFromCategory}
        title="Удалить товар из категории?"
        message={categoryToRemove ? `Вы уверены, что хотите удалить товар из категории "${categoryToRemove.name}"?` : ''}
        secondButtonText="Удалить"
        secondButtonAction={confirmRemoveFromCategory}
        isLoading={removeFromCategory.isPending}
      />}

      {isDeleteProductModalOpen && <DoubleCheckModal
        isOpen={isDeleteProductModalOpen}
        onClose={cancelDelete}
        title="Удалить товар?"
        message={`Вы уверены, что хотите удалить товар "${product.name}"?`}
        secondButtonText="Удалить"
        secondButtonAction={confirmDelete}
        isLoading={deleteProduct.isPending}
      />}
    </div>
  );
}

